'use client';

import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import { Eye, EyeOff, Zap, Copy, CheckCheck, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { DEMO_TODAY, demoCredentials } from '@/lib/demo/demoData';
import { getCurrentUser, loginStaff, loginStudent } from '@/lib/demo/demoAuth';
import { useDemoData } from '@/lib/demo/demoHooks';

type Role = 'alumno' | 'profesor' | 'admin';

interface StudentForm {
  dni: string;
}

interface StaffForm {
  email: string;
  password: string;
}

export default function LoginScreen() {
  const router = useRouter();
  const demoData = useDemoData();
  const [role, setRole] = useState<Role>('alumno');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const studentForm = useForm<StudentForm>({ mode: 'onSubmit' });
  const staffForm = useForm<StaffForm>({ mode: 'onSubmit' });

  useEffect(() => {
    const session = getCurrentUser();
    if (session?.role === 'student') router.replace('/student-home-screen');
    if (session?.role === 'admin' || session?.role === 'professor') router.replace('/admin-dashboard');
  }, [router]);

  const handleCopy = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const autofillStudent = () => {
    studentForm.setValue('dni', demoCredentials.student.dni);
  };

  const autofillStaff = (r: 'profesor' | 'admin') => {
    const creds = demoCredentials[r === 'profesor' ? 'professor' : 'admin'];
    staffForm.setValue('email', creds.email);
    staffForm.setValue('password', creds.password);
  };

  const handleStudentSubmit = async (data: StudentForm) => {
    setLoginError('');
    setIsLoading(true);
    await new Promise(r => setTimeout(r, 900));
    if (loginStudent(data.dni)) {
      toast.success('Bienvenido, Lucía Fernández');
      router.push('/student-home-screen');
    } else {
      setLoginError('DNI no encontrado — usá el DNI de demo abajo para ingresar');
    }
    setIsLoading(false);
  };

  const handleStaffSubmit = async (data: StaffForm) => {
    setLoginError('');
    setIsLoading(true);
    await new Promise(r => setTimeout(r, 900));
    if (role === 'profesor' && loginStaff('professor', data.email, data.password)) {
      toast.success('Bienvenido, Prof. Martínez');
      router.push('/admin-dashboard');
    } else if (role === 'admin' && loginStaff('admin', data.email, data.password)) {
      toast.success('Bienvenido, Admin');
      router.push('/admin-dashboard');
    } else {
      setLoginError('Credenciales inválidas — usá las cuentas demo de abajo para ingresar');
    }
    setIsLoading(false);
  };

  const roles: { key: Role; label: string }[] = [
    { key: 'alumno', label: 'ALUMNO' },
    { key: 'profesor', label: 'PROFESOR' },
    { key: 'admin', label: 'ADMIN' },
  ];

  return (
    <div className="min-h-screen bg-background flex flex-col lg:flex-row overflow-hidden relative">
      {/* Scanline effect */}
      <div className="scanline fixed inset-0 w-full h-8 z-0 pointer-events-none" />

      {/* LEFT PANEL — Brand */}
      <div className="hidden lg:flex lg:w-1/2 xl:w-[55%] flex-col justify-between p-12 xl:p-16 relative overflow-hidden border-r border-border">
        {/* Accent blob */}
        <div className="blob-accent absolute top-1/3 left-1/4 w-96 h-96" />

        {/* Logo */}
        <div className="flex items-center gap-3 relative z-10">
          <div className="w-10 h-10 bg-foreground flex items-center justify-center">
            <span className="text-background font-black text-lg leading-none">G</span>
          </div>
          <span className="text-foreground font-black text-xl tracking-[0.15em] uppercase">
            GymOS
          </span>
          <span className="text-[9px] tracking-[0.12em] text-accent border border-accent/30 px-1.5 py-0.5">DEMO</span>
        </div>

        {/* Hero copy */}
        <div className="relative z-10 space-y-8">
          <div>
            <p className="text-label-caps text-muted-foreground mb-4">Sistema de Gestión</p>
            <h1 className="text-hero text-foreground">
              EL DOLOR<br />
              ES<br />
              <span className="text-accent">TEMPORAL.</span>
            </h1>
            <h2 className="text-hero text-foreground mt-2">
              LA GLORIA<br />
              ES ETERNA.
            </h2>
          </div>
          <div className="flex items-center gap-4">
            <div className="h-px bg-border flex-1" />
            <p className="text-muted-foreground text-sm font-medium">2 SUCURSALES · BUENOS AIRES</p>
            <div className="h-px bg-border flex-1" />
          </div>
          <div className="grid grid-cols-3 gap-4">
            {[
              { value: String(demoData.members.filter(member => member.status === 'Activo').length), label: 'Alumnos Activos' },
              { value: String(demoData.branches.length), label: 'Sucursales' },
              { value: `${Math.round((demoData.attendances.filter(attendance => attendance.attendedAt.startsWith(DEMO_TODAY)).length / demoData.members.filter(member => member.status === 'Activo').length) * 100)}%`, label: 'Asistencia Hoy' },
            ].map((stat) => (
              <div key={`stat-${stat.label}`} className="border-l-2 border-accent pl-4">
                <p className="text-2xl font-black font-tabular text-foreground">{stat.value}</p>
                <p className="text-label-caps text-muted-foreground mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom */}
        <div className="relative z-10">
          <p className="text-label-caps text-muted-foreground">
            PALERMO · BELGRANO © 2026
          </p>
        </div>
      </div>

      {/* RIGHT PANEL — Form */}
      <div className="flex-1 flex flex-col justify-center px-6 py-12 sm:px-10 lg:px-12 xl:px-16 relative z-10">
        {/* Mobile logo */}
        <div className="flex items-center gap-3 mb-10 lg:hidden">
          <div className="w-9 h-9 bg-foreground flex items-center justify-center">
            <span className="text-background font-black text-base leading-none">G</span>
          </div>
          <span className="text-foreground font-black text-lg tracking-[0.15em] uppercase">GymOS</span>
          <span className="text-[9px] tracking-[0.12em] text-accent border border-accent/30 px-1.5 py-0.5">DEMO</span>
        </div>

        <div className="w-full max-w-md mx-auto lg:mx-0">
          <p className="text-label-caps text-muted-foreground mb-2">Acceso al Sistema</p>
          <h2 className="text-display text-foreground mb-8">INGRESAR</h2>

          {/* Role Tabs */}
          <div className="flex border border-border mb-8">
            {roles.map((r) => (
              <button
                key={`role-tab-${r.key}`}
                onClick={() => { setRole(r.key); setLoginError(''); }}
                className={`flex-1 py-3 text-label-caps transition-all duration-200 ${
                  role === r.key
                    ? 'bg-foreground text-background'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>

          {/* Error message */}
          {loginError && (
            <div className="flex items-start gap-3 p-4 border border-danger bg-danger/10 mb-6 fade-in">
              <AlertCircle size={16} className="text-danger mt-0.5 shrink-0" />
              <p className="text-sm text-danger leading-snug">{loginError}</p>
            </div>
          )}

          {/* ALUMNO FORM */}
          {role === 'alumno' && (
            <form onSubmit={studentForm.handleSubmit(handleStudentSubmit)} className="space-y-6 fade-in">
              <div>
                <label className="text-label-caps text-muted-foreground block mb-2">
                  DNI / Número de Documento
                </label>
                <input
                  type="number"
                  autoFocus
                  className="input-brutal w-full h-14 px-4 text-xl font-bold font-tabular tracking-widest"
                  placeholder="38291047"
                  {...studentForm.register('dni', {
                    required: 'Ingresá tu DNI',
                    minLength: { value: 7, message: 'DNI inválido' },
                    maxLength: { value: 9, message: 'DNI inválido' },
                  })}
                />
                {studentForm.formState.errors.dni && (
                  <p className="text-danger text-sm mt-2">{studentForm.formState.errors.dni.message}</p>
                )}
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="btn-accent w-full h-14 text-sm flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <div className="w-5 h-5 border-2 border-accent-foreground/30 border-t-accent-foreground rounded-full animate-spin" />
                ) : (
                  <>
                    <Zap size={18} />
                    ACCESO RÁPIDO
                  </>
                )}
              </button>

              {/* Demo credentials */}
              <div className="border border-border p-4 space-y-3">
                <p className="text-label-caps text-muted-foreground">Demo — Alumno</p>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">DNI</p>
                    <p className="font-mono font-bold text-foreground">{demoCredentials.student.dni}</p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => handleCopy(demoCredentials.student.dni, 'dni')}
                      className="p-2 border border-border hover:border-foreground/30 transition-colors"
                    >
                      {copiedField === 'dni' ? <CheckCheck size={14} className="text-accent" /> : <Copy size={14} className="text-muted-foreground" />}
                    </button>
                    <button
                      type="button"
                      onClick={autofillStudent}
                      className="px-3 py-2 text-label-caps border border-border hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
                    >
                      USAR
                    </button>
                  </div>
                </div>
              </div>
            </form>
          )}

          {/* STAFF FORM (Profesor / Admin) */}
          {(role === 'profesor' || role === 'admin') && (
            <form onSubmit={staffForm.handleSubmit(handleStaffSubmit)} className="space-y-5 fade-in">
              <div>
                <label className="text-label-caps text-muted-foreground block mb-2">Email</label>
                <input
                  type="email"
                  autoFocus
                  className="input-brutal w-full h-12 px-4 text-sm"
                  placeholder={role === 'admin' ? 'admin@gymos.ar' : 'profesor@gymos.ar'}
                  {...staffForm.register('email', {
                    required: 'Ingresá tu email',
                    pattern: { value: /^\S+@\S+\.\S+$/, message: 'Email inválido' },
                  })}
                />
                {staffForm.formState.errors.email && (
                  <p className="text-danger text-sm mt-2">{staffForm.formState.errors.email.message}</p>
                )}
              </div>

              <div>
                <label className="text-label-caps text-muted-foreground block mb-2">Contraseña</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    className="input-brutal w-full h-12 px-4 pr-12 text-sm"
                    placeholder="••••••••"
                    {...staffForm.register('password', {
                      required: 'Ingresá tu contraseña',
                      minLength: { value: 6, message: 'Mínimo 6 caracteres' },
                    })}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {staffForm.formState.errors.password && (
                  <p className="text-danger text-sm mt-2">{staffForm.formState.errors.password.message}</p>
                )}
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="btn-primary w-full h-14 text-sm flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <div className="w-5 h-5 border-2 border-background/30 border-t-background rounded-full animate-spin" />
                ) : (
                  `INGRESAR COMO ${role === 'admin' ? 'ADMINISTRADOR' : 'PROFESOR'}`
                )}
              </button>

              {/* Demo credentials */}
              <div className="border border-border p-4 space-y-3">
                <p className="text-label-caps text-muted-foreground">
                  Demo — {role === 'admin' ? 'Administrador' : 'Profesor'}
                </p>
                {(['email', 'password'] as const).map((field) => {
                  const creds = demoCredentials[role === 'profesor' ? 'professor' : 'admin'];
                  return (
                    <div key={`cred-${role}-${field}`} className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-muted-foreground mb-1 capitalize">{field === 'email' ? 'Email' : 'Contraseña'}</p>
                        <p className="font-mono text-sm text-foreground">{creds[field]}</p>
                      </div>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => handleCopy(creds[field], `${role}-${field}`)}
                          className="p-2 border border-border hover:border-foreground/30 transition-colors"
                        >
                          {copiedField === `${role}-${field}` ? <CheckCheck size={14} className="text-accent" /> : <Copy size={14} className="text-muted-foreground" />}
                        </button>
                      </div>
                    </div>
                  );
                })}
                <button
                  type="button"
                  onClick={() => autofillStaff(role as 'profesor' | 'admin')}
                  className="w-full py-2 text-label-caps border border-border hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
                >
                  AUTOCOMPLETAR DEMO
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
